import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { EmptyState } from "@/components/common/EmptyState";
import { LeadCard } from "@/components/common/LeadCard";
import { SelectField } from "@/components/common/Field";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { STATUS_ORDER, statusMeta } from "@/lib/constants";
import { todayISO } from "@/lib/format";
import { Plus, Search, Car, X, SlidersHorizontal } from "lucide-react";

const TYPE_OPTS = [
  { value: "all", label: "All types" },
  { value: "dealership", label: "Dealership" },
  { value: "individual", label: "Individual" },
];
const STATUS_OPTS = [
  { value: "all", label: "All statuses" },
  ...STATUS_ORDER.map((s) => ({ value: s, label: statusMeta(s).label })),
];
const DEAL_OPTS = [
  { value: "all", label: "All deal stages" },
  { value: "documents_pending", label: "Documents pending" },
  { value: "approval_pending", label: "Approval pending" },
  { value: "payment_pending", label: "Payment pending" },
  { value: "cod_pending", label: "COD pending" },
  { value: "completed", label: "Completed" },
];

export default function Leads() {
  const { isLeader } = useAuth();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const [search, setSearch] = useState(sp.get("search") || "");
  const [type, setType] = useState(sp.get("type") || "all");
  const [status, setStatus] = useState(sp.get("status") || "all");
  const [deal, setDeal] = useState(sp.get("deal") || "all");
  const [employee, setEmployee] = useState(sp.get("employee") || "all");
  const due = sp.get("due");

  useEffect(() => {
    if (isLeader) api.get("/users").then((res) => setEmployees(res.data.filter((u) => u.role === "employee")));
  }, [isLeader]);

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (type !== "all") params.lead_type = type;
    if (status !== "all") params.status = status;
    if (deal !== "all") params.deal_status = deal;
    if (isLeader && employee !== "all") params.employee = employee;
    api.get("/leads", { params })
      .then((res) => {
        let rows = res.data;
        if (due === "today") {
          const t = todayISO();
          rows = rows.filter((l) => l.next_follow_up === t && !["DEAL_DONE", "LOST"].includes(l.status));
        }
        setLeads(rows);
      })
      .finally(() => setLoading(false));
  }, [search, type, status, deal, employee, isLeader, due]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const activeFilters = [type, status, deal].filter((v) => v !== "all").length + (employee !== "all" ? 1 : 0) + (due ? 1 : 0);

  const clearAll = () => {
    setSearch(""); setType("all"); setStatus("all"); setDeal("all"); setEmployee("all");
    setSp({}, { replace: true });
  };

  return (
    <div className="animate-fade-in-up space-y-4">
      <PageHeader
        title={isLeader ? "All Leads" : "My Leads"}
        subtitle={`${leads.length} lead${leads.length === 1 ? "" : "s"}${due === "today" ? " · follow-up due today" : ""}`}
        actions={
          <Button variant="brand" onClick={() => navigate("/leads/new")}>
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New Lead</span>
          </Button>
        }
      />

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="h-11 pl-9" placeholder="Search vehicle no, name, model, lead ID…"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Button variant={showFilters || activeFilters ? "default" : "outline"} size="icon"
          onClick={() => setShowFilters((s) => !s)} className="relative shrink-0">
          <SlidersHorizontal className="h-4 w-4" />
          {activeFilters > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-brand-foreground">{activeFilters}</span>
          )}
        </Button>
      </div>

      {showFilters && (
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-border bg-card p-3 shadow-card sm:grid-cols-3">
          <SelectField value={type} onChange={setType} options={TYPE_OPTS} />
          <SelectField value={status} onChange={setStatus} options={STATUS_OPTS} />
          <SelectField value={deal} onChange={setDeal} options={DEAL_OPTS} />
          {isLeader && (
            <SelectField className="col-span-2 sm:col-span-3" value={employee} onChange={setEmployee}
              options={[{ value: "all", label: "All employees" }, ...employees.map((e) => ({ value: e.id, label: e.full_name }))]} />
          )}
          {(activeFilters > 0 || search) && (
            <Button variant="ghost" size="sm" className="col-span-2 justify-start text-destructive sm:col-span-3" onClick={clearAll}>
              <X className="h-4 w-4" /> Clear all filters
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <Loader />
      ) : leads.length ? (
        <div className="space-y-2.5">
          {leads.map((l) => <LeadCard key={l.id} lead={l} showOwner={isLeader} />)}
        </div>
      ) : (
        <EmptyState icon={Car} title="No leads found"
          description="Try adjusting filters or create a new lead."
          action={<Button variant="brand" onClick={() => navigate("/leads/new")}><Plus className="h-4 w-4" /> New Lead</Button>} />
      )}
    </div>
  );
}
